import useScrollReset from "@hooks/ScrollHook";
import { NftTabs } from "@components/tabs/NftTabs";
import { Link, useNavigate, useParams } from "react-router-dom";
import { errorPromise, useApi } from "@hooks/ApiHook";
import ActionDropdown from "@components/dropdown/ActionDropdown";
import { TraitsTab } from "@app/item/tabs/TraitsTab";
import { SelectableCoinBadge } from "@components/SelectableCoinBadge";
import { useEffect, useState } from "react";
import { useAccount } from "@contexts/AccountContext";
import useAlert from "@hooks/AlertHook";
import { useWasmX } from "@hooks/WasmXHook";
import { useSelectedChain } from "@app/examples/ChainSelector";
import { useCoins } from "@contexts/CoinsContext";
import { TxInfo } from "@terra-money/terra.js";
import { useTxModal } from "@hooks/TxModalHook";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";
import { AssetRequest, MessageDetail } from "wasmx";
import { useWallet } from "@providers/wallet.provider";
import { querySmartContract } from "@helpers/rpc.helper";
import { signWithAnyWallet } from "@helpers/amino.helper";

export function MintableItemPage() {
  useScrollReset();
  const { id } = useParams();
  const navigate = useNavigate();
  const { address } = useAccount();
  const alert = useAlert();
  const wasmX = useWasmX();
  const { selectedChain } = useSelectedChain();
  const { getCoin } = useCoins();
  const txModal = useTxModal();

  const [amount, setAmount] = useState(1);
  const [payment, setPayment] = useState<string>();
  const { wallet, chain } = useWallet();
  const { data: item } = useApi((api) => api.item.find(id!));
  const { data: supply } = useApi((api) => api.item.itemSupply(id!));
  const { data: traits } = useApi((api) => api.item.itemTraits(id!));
  const { data: collection } = useApi(
    (api) => {
      if (!item) return errorPromise();
      return api.collection.find(item.collectionId);
    },
    [item]
  );
  const { data: mintableDetail } = useApi((api) => api.item.listDetail(id!));

  useEffect(() => {
    if (address && collection?.ownerAddress === address)
      navigate(`/item/${id}`, { replace: true });
  }, [collection, address]);

  const onMint = async () => {
    try {
      if (!supply) throw new Error("Cannot load supply info");
      if (!payment) throw new Error("Please select payment type");
      if (!wasmX || !selectedChain)
        throw new Error("Cannot read chain information");
      if (!address)
        throw new Error(
          "Cannot load user's information, try to refresh the page"
        );
      if (!mintableDetail || !item || !collection)
        throw new Error(
          "Cannot load item's information, try to refresh the page"
        );
      if (amount > supply.available)
        throw new Error(`Max amount to mint is ${supply.available}`);
      if (amount < 1) throw new Error(`Min amount is 1`);
      console.log(mintableDetail);
      const priceEntry = mintableDetail.prices!.find(
        (e) => e.coinId === payment
      );
      if (!priceEntry)
        throw new Error("Selected payment not available for this item");

      // total price (sudah dikali amount)
      const unitPrice = priceEntry.price; // asumsi sudah dalam "atomics" denom/token
      const mintPrice = (unitPrice * amount).toString();

      const pricesOnChain: AssetRequest[] = mintableDetail.prices!.map((e) => {
        const c = getCoin(e.coinId)!;
        return c.isCw20
          ? { token: { address: c.cw20Address!, amount: e.price.toString() } }
          : { coin: { denom: c.denom!, amount: e.price.toString() } };
      });
      const { voucher: voucherJson } = await querySmartContract(
        collection.collectionContract,
        {
          generate_voucher: {
            voucher: {
              metadata: {
                animation_url: item.animationUrl,
                background_color: item.backgroundColor,
                description: item.description,
                external_url: item.externalUrl,
                identifier: item.id,
                image: item.image,
                image_data: item.imageData,
                name: item.name,
                supply: item.supply,
                token_uri: item.tokenUri,
                youtube_url: item.youtubeUrl,
                attributes: traits?.map((t) => ({
                  trait_type: t.traitType!,
                  value: t.value!,
                  display_type: t.displayType,
                })),
              },
              prices: pricesOnChain,
            },
          },
        }
      );
      if (wallet) {
        const a = await signWithAnyWallet(wallet, {
          message: voucherJson,
          address: address,
        });

        const selected = getCoin(payment)!;
        const msgs: MsgExecuteContract<any>[] = [];
        let msg = {
          mint: {
            owner: address,
            amount,
            voucher: voucherJson, // <— hanya signature
            wallet_type: "cosmes",
            pubkey: a.pubkeyBase64,
            extension: {
              animation_url: item.animationUrl,
              attributes: traits?.map((t) => ({
                trait_type: t.traitType!,
                value: t.value!,
                display_type: t.displayType,
              })),
              background_color: item.backgroundColor,
              description: item.description,
              external_url: item.externalUrl,
              identifier: item.id,
              image: item.image,
              image_data: item.imageData,
              name: item.name,
              supply: item.supply,
              token_uri: item.tokenUri,
              youtube_url: item.youtubeUrl,
            },
            signature: a.signatureBase64,
            prices: pricesOnChain, // unit price; kontrak yg kalikan amount
          },
        };
        if (selected.isCw20 && selected.cw20Address) {
          msgs.push(
            new MsgExecuteContract({
              sender: address,
              contract: selected.cw20Address,
              msg: {
                send: {
                  msg: Buffer.from(JSON.stringify(msg)).toString("base64"),
                  amount: mintPrice,
                  contract: collection.collectionContract,
                },
              },
              funds: [],
            })
          );
        } else {
          msgs.push(
            new MsgExecuteContract({
              contract: collection.collectionContract,
              sender: address,
              msg: msg,
              funds: [
                {
                  amount: mintPrice,
                  denom: selected.denom!,
                },
              ],
            })
          );
        }
        console.log(msgs);

        await new Promise<string>((onSuccess, onError) =>
          txModal.show(msgs, `Mint '${item.name}'`, {
            onSuccess,
            onError,
          })
        );

        navigate("/profile");
        alert.success("Item minted successfully");
      }
    } catch (e) {
      console.error(JSON.stringify(e));
      if (e instanceof Error) alert.failed(e.message);
      else throw e;
    }
  };

  return (
    <main className="mt-24">
      <section className="relative pb-24 pt-12 lg:py-24">
        <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
          <img
            src="/src/img/gradient_light.jpg"
            alt="gradient"
            className="h-full w-full"
          />
        </picture>
        <div className="container">
          <div className="md:flex md:flex-wrap">
            <figure className="mb-8 md:w-2/5 md:flex-shrink-0 md:flex-grow-0 md:basis-auto lg:w-1/2">
              <img
                src={item?.image}
                alt="item"
                className="w-full cursor-pointer rounded-2.5xl"
              />
            </figure>

            <div className="md:w-3/5 md:basis-auto md:pl-8 lg:w-1/2 lg:pl-[3.75rem]">
              <div className="mb-3 flex">
                <div className="flex items-center">
                  <Link
                    to={`/collection/${item?.collection?.slug}`}
                    className="mr-2 text-lg font-bold text-jacarta-500"
                  >
                    {item?.collection?.name}
                  </Link>
                </div>
              </div>

              <h1 className="mb-4 font-display text-4xl font-semibold text-jacarta-700 dark:text-white">
                {item?.name}
              </h1>

              <p className="mb-5 dark:text-gray-400">{item?.description}</p>

              <div className="mb-2 flex flex-wrap">
                <Link
                  to={`/user/${item?.collection?.owner?.username}`}
                  className="mb-4 mr-8 flex"
                >
                  <figure className="mr-4 shrink-0">
                    <div className="relative block">
                      <img
                        src={
                          item?.collection?.owner?.thumbUrl ??
                          "/src/img/logo/transparant_logo.png"
                        }
                        alt="avatar 7"
                        className="w-12 rounded-2lg"
                        loading="lazy"
                      />
                    </div>
                  </figure>
                  <div className="flex flex-col justify-center">
                    <span className="block text-sm text-jacarta-400 dark:text-white">
                      Creator
                    </span>
                    <div className="block text-jacarta-400">
                      <span className="text-sm font-bold">
                        {item?.collection?.owner?.username}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>

              {mintableDetail && (
                <div className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900">
                  <div className="flex flex-wrap">
                    {mintableDetail.prices?.map((price) => (
                      <div className="mb-2.5 mr-2.5" key={price.coinId}>
                        <SelectableCoinBadge
                          onSelected={(coinId) =>
                            setPayment(coinId === payment ? undefined : coinId)
                          }
                          isSelected={price.coinId == payment}
                          amount={price.price}
                          coinId={price.coinId}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mb-5">
                    <label
                      htmlFor="item-supply"
                      className="block font-display text-jacarta-700 dark:text-white"
                    >
                      Amount
                    </label>
                    <p className="mb-3 text-2xs dark:text-gray-400">
                      Available ({supply?.available}/{supply?.total})
                    </p>
                    <input
                      value={amount}
                      onChange={({ target }) => setAmount(target.valueAsNumber)}
                      type="number"
                      id="item-supply"
                      className="w-full rounded-lg border-jacarta-100 py-3 hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500 dark:border-gray-400 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-400"
                      placeholder="Amount"
                      required
                    />
                  </div>

                  <button
                    onClick={onMint}
                    className="inline-block w-full rounded-full bg-jacarta-500 px-8 py-3 text-center font-semibold text-white transition-all hover:bg-jacarta-600"
                  >
                    Mint
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="scrollbar-custom mt-14 overflow-x-auto rounded-lg">
            <div className="min-w-fit">
              <NftTabs
                items={{
                  Traits: traits && <TraitsTab traits={traits} />,
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
